/* Chat (port React del modulo playtomic-web) — tema chiaro.
   Lista conversazioni (nome+avatar+uid, marcatori 🟢 da rispondere / 🔵 gestita,
   filtro, paginazione) + vista messaggi (recente in alto) + invio. */
import { useEffect, useMemo, useRef, useState } from "react";
import { ptapp, type Account, type ChatThread, type ChatMsg } from "@/lib/ptapp";

type NameRec = { name: string; picture: string | null };
const PAGE = 25;

const fmt = (d?: number | string) =>
  d ? new Date(typeof d === "number" ? d : Date.parse(String(d))).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
const msgText = (m: ChatMsg) =>
  m.data?.text || (m.data?.type === "system" ? "· " + (m.data?.category || "sistema") : "") || "";

function Avatar({ pic, size }: { pic?: string | null; size: number }) {
  if (pic) return <img src={pic} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" alt="" />;
  return <div style={{ width: size, height: size, fontSize: Math.round(size * 0.5) }} className="rounded-full bg-slate-200 flex items-center justify-center shrink-0">👤</div>;
}

export default function Chat() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [acct, setAcct] = useState("staff");
  const [meUid, setMeUid] = useState("");
  const [threads, setThreads] = useState<ChatThread[] | null>(null);
  const [names, setNames] = useState<Record<string, NameRec>>({});
  const [engaged, setEngaged] = useState<Record<string, boolean | null>>({});
  const [shown, setShown] = useState(PAGE);
  const [onlyReply, setOnlyReply] = useState(false);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState<ChatThread | null>(null);
  const namesRef = useRef<Record<string, NameRec>>({});

  useEffect(() => { ptapp.accounts().then(setAccounts).catch(() => {}); }, []);

  // carica thread + uid quando cambia account
  useEffect(() => {
    let alive = true;
    setThreads(null); setSel(null); setShown(PAGE); setOnlyReply(false); setErr("");
    namesRef.current = {}; setNames({}); setEngaged({});
    ptapp.uid(acct).then((u) => alive && setMeUid(String(u.uid))).catch(() => {});
    ptapp.threads(acct).then((t) => alive && setThreads(Array.isArray(t) ? t : []))
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [acct]);

  // helpers DM/markers
  const otherUid = (t: ChatThread) => { const u = (t.users || []).map(String).filter((x) => x && x !== meUid); return u.length === 1 ? u[0] : null; };
  const isDM = (t: ChatThread) => String(t.chat_id || "").startsWith("u:") || (!t.name && !t.resource && !!otherUid(t));
  const dmName = (o: string) => (names[o]?.name && names[o].name !== o ? names[o].name : "Cliente " + o);
  const title = (t: ChatThread) => t.name || (isDM(t) && otherUid(t) ? dmName(otherUid(t)!) : [t.resource, fmt(t.match_date)].filter(Boolean).join(" · ") || t.chat_id);
  const awaiting = (t: ChatThread) => isDM(t) && t.last_user_id != null && String(t.last_user_id) !== meUid && !t.last_is_system;
  const handled = (t: ChatThread) => isDM(t) && !awaiting(t) && engaged[t.chat_id] === true;

  const view = useMemo(() => (threads || []).filter((t) => (onlyReply ? awaiting(t) : true)), [threads, onlyReply, meUid]);
  const awaitingTot = useMemo(() => (threads || []).filter(awaiting).length, [threads, meUid]);
  const pageItems = view.slice(0, shown);

  // idrata nomi + engaged per la pagina visibile
  useEffect(() => {
    let alive = true;
    const needN = [...new Set(pageItems.filter(isDM).map(otherUid).filter((o): o is string => !!o && !(o in namesRef.current)))];
    if (needN.length) ptapp.names(acct, needN).then((r) => {
      if (!alive) return; Object.assign(namesRef.current, r || {}); needN.forEach((o) => { if (!(o in namesRef.current)) namesRef.current[o] = { name: o, picture: null }; });
      setNames({ ...namesRef.current });
    }).catch(() => {});
    const needE = [...new Set(pageItems.filter((t) => isDM(t) && !awaiting(t) && !(t.chat_id in engaged)).map((t) => t.chat_id))];
    if (needE.length) ptapp.engaged(acct, needE).then((r) => alive && setEngaged((e) => ({ ...e, ...(r || {}) }))).catch(() => {});
    return () => { alive = false; };
  }, [acct, shown, view.length, meUid]);

  return (
    <div className="mx-auto max-w-6xl p-4">
      <div className="mb-3 flex items-center gap-3">
        <h2 className="text-lg font-bold text-slate-900">Chat</h2>
        <select value={acct} onChange={(e) => setAcct(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
          {accounts.map((a) => <option key={a.key} value={a.key}>{a.name || a.email}</option>)}
        </select>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* lista */}
        <div className="lg:w-[380px] lg:shrink-0">
          <div className="mb-2 rounded-lg border border-slate-200 bg-white p-2.5">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={onlyReply} onChange={(e) => { setOnlyReply(e.target.checked); setShown(PAGE); }} />
              🟢 solo dove ha risposto il cliente
              <span className="ml-auto rounded-full bg-slate-100 px-2 text-xs text-slate-600">{awaitingTot}</span>
            </label>
            <div className="mt-1 text-xs text-slate-400">🟢 da rispondere · 🔵 cliente ha risposto (gestita)</div>
          </div>

          {threads == null ? <div className="text-sm text-slate-500">carico…</div> :
            err ? <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div> :
              view.length === 0 ? <div className="text-sm text-slate-500">Nessuna conversazione.</div> : (
                <div className="max-h-[72vh] space-y-2 overflow-auto pr-1">
                  {pageItems.map((t) => {
                    const aw = awaiting(t), hd = handled(t), o = isDM(t) ? otherUid(t) : null;
                    return (
                      <button key={t.chat_id} onClick={() => setSel(t)}
                        className={"w-full rounded-lg border bg-white p-2.5 text-left hover:bg-slate-50 " +
                          (sel?.chat_id === t.chat_id ? "border-moving-500 ring-1 ring-moving-500" : "border-slate-200")}
                        style={aw ? { borderLeft: "3px solid #16a34a" } : hd ? { borderLeft: "3px solid #2563eb" } : undefined}>
                        <div className="flex items-center gap-2">
                          <Avatar pic={o ? names[o]?.picture : null} size={30} />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center justify-between gap-2">
                              <b className="truncate text-sm text-slate-900">{aw ? "🟢 " : hd ? "🔵 " : ""}{String(title(t)).slice(0, 30)}</b>
                              <span className="shrink-0 text-xs text-slate-400">{fmt(t.last_date)}</span>
                            </span>
                            {o ? <span className="block text-[11px] text-slate-400">uid {o}</span> : null}
                          </span>
                        </div>
                        <div className="mt-1 truncate text-xs text-slate-500">{aw ? <b className="text-green-700">↩ </b> : null}{(t.last_text || "").slice(0, 55) || "—"}</div>
                      </button>
                    );
                  })}
                  {shown < view.length && (
                    <button onClick={() => setShown((s) => s + PAGE)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                      Carica più chat ({view.length - shown} rimaste)
                    </button>
                  )}
                </div>
              )}
        </div>

        {/* conversazione */}
        <div className="min-w-0 flex-1">
          {sel ? <Conversation acct={acct} t={sel} meUid={meUid} names={names} setNames={setNames} namesRef={namesRef} title={title} isDM={isDM} otherUid={otherUid} />
            : <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">Seleziona una conversazione.</div>}
        </div>
      </div>
    </div>
  );
}

function Conversation({ acct, t, meUid, names, setNames, namesRef, title, isDM, otherUid }: {
  acct: string; t: ChatThread; meUid: string; names: Record<string, NameRec>;
  setNames: (n: Record<string, NameRec>) => void; namesRef: React.MutableRefObject<Record<string, NameRec>>;
  title: (t: ChatThread) => string; isDM: (t: ChatThread) => boolean; otherUid: (t: ChatThread) => string | null;
}) {
  const [msgs, setMsgs] = useState<ChatMsg[] | null>(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    setMsgs(null);
    ptapp.msgs(acct, t.chat_id).then(async (m) => {
      const list = Array.isArray(m) ? m : [];
      const need = [...new Set([...(t.users || []), ...list.map((x) => String(x.user_id || x.data?.["user_id" as never] || ""))].map(String).filter((u) => u && !(u in namesRef.current)))];
      if (need.length) { try { const r = await ptapp.names(acct, need); Object.assign(namesRef.current, r || {}); setNames({ ...namesRef.current }); } catch { /* */ } }
      setMsgs(list);
    }).catch(() => setMsgs([]));
  };
  useEffect(load, [acct, t.chat_id]);

  const nm = (u: string) => names[u]?.name || u;
  const send = async () => {
    const v = text.trim(); if (!v) return; setSending(true);
    try { await ptapp.sendMsg(acct, t.chat_id, v); setText(""); load(); }
    catch (e: any) { alert("Errore invio: " + e.message); } finally { setSending(false); }
  };
  const ho = isDM(t) ? otherUid(t) : null;

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex items-center gap-3">
          <Avatar pic={ho ? names[ho]?.picture : null} size={40} />
          <div className="min-w-0">
            <b className="text-slate-900">{title(t)}</b>{ho ? <span className="ml-2 text-xs text-slate-400">uid {ho}</span> : null}
            <div className="truncate text-xs text-slate-500">{t.resource ? t.resource + " · " : ""}partecipanti: {(t.users || []).map(nm).join(", ") || "-"}</div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Scrivi un messaggio…" className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm" />
          <button onClick={send} disabled={sending} className="rounded-md bg-moving-500 px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">Invia</button>
        </div>
        <div className="mt-1.5 text-xs text-slate-400">La cancellazione dei messaggi va fatta dall'app Playtomic.</div>
      </div>

      {msgs == null ? <div className="text-sm text-slate-500">carico…</div> :
        msgs.length === 0 ? <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">Nessun messaggio.</div> : (
          <div className="space-y-2">
            {msgs.slice().reverse().map((m) => {
              const who = String(m.user_id || ""); const mine = who === meUid;
              return (
                <div key={m.id} className={"rounded-lg border bg-white p-2.5 " + (mine ? "border-blue-300" : "border-slate-200")}>
                  <div className="text-xs text-slate-400">{mine ? "Tu" : nm(who)} · {fmt(m.date)}</div>
                  <div className="text-sm text-slate-800">{msgText(m)}</div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
